const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 5_000;
const RESUME_DEBOUNCE_MS = 1_000;

let timer: number | null = null;
let inFlightController: AbortController | null = null;
let inFlightPromise: Promise<void> | null = null;
let running = false;
let listenersInstalled = false;
let lastHeartbeatSucceededAt = 0;
let lastResumeAt = 0;

function canUseDom(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function clearTimer(): void {
  if (timer === null) return;
  try { window.clearTimeout(timer); } catch {}
  timer = null;
}

function isPageVisible(): boolean {
  if (!canUseDom()) return true;
  return document.visibilityState !== "hidden";
}

function isNavigatorOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

function shouldPause(): boolean {
  return !running || !isPageVisible() || !isNavigatorOnline();
}

function abortInFlight(): void {
  const controller = inFlightController;
  if (!controller) return;
  try { controller.abort(); } catch {}
}

function scheduleNext(delayMs: number = HEARTBEAT_INTERVAL_MS): void {
  if (!running || timer !== null || shouldPause()) return;

  timer = window.setTimeout(() => {
    timer = null;
    void heartbeatOnce();
  }, Math.max(250, delayMs));
}

async function heartbeatOnce(): Promise<void> {
  if (!running || shouldPause()) return;
  if (inFlightPromise) return inFlightPromise;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    try { controller.abort(); } catch {}
  }, HEARTBEAT_TIMEOUT_MS);

  inFlightController = controller;
  inFlightPromise = (async () => {
    try {
      const res = await fetch("/api/online/heartbeat", {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
      });

      if (res.status === 401 || res.status === 403) {
        stopOnlineHeartbeat();
        return;
      }

      if (res.ok) {
        lastHeartbeatSucceededAt = Date.now();
      }
    } catch {}
    finally {
      try { window.clearTimeout(timeoutId); } catch {}
      if (inFlightController === controller) {
        inFlightController = null;
      }
      inFlightPromise = null;

      if (running) {
        if (shouldPause()) {
          clearTimer();
        } else {
          scheduleNext(HEARTBEAT_INTERVAL_MS);
        }
      }
    }
  })();

  return inFlightPromise;
}

function resumeHeartbeat(): void {
  if (!running || shouldPause()) return;

  const now = Date.now();
  if (now - lastResumeAt < RESUME_DEBOUNCE_MS) return;
  lastResumeAt = now;

  clearTimer();

  if (inFlightPromise) return;

  const elapsedSinceSuccess = lastHeartbeatSucceededAt > 0
    ? now - lastHeartbeatSucceededAt
    : Number.POSITIVE_INFINITY;

  if (elapsedSinceSuccess >= HEARTBEAT_INTERVAL_MS) {
    void heartbeatOnce();
    return;
  }

  scheduleNext(HEARTBEAT_INTERVAL_MS - elapsedSinceSuccess);
}

function installLifecycleListeners(): void {
  if (listenersInstalled || !canUseDom()) return;
  listenersInstalled = true;

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      clearTimer();
      abortInFlight();
      return;
    }

    resumeHeartbeat();
  };

  const onPageHide = () => {
    clearTimer();
    abortInFlight();
  };

  const onPageShow = () => resumeHeartbeat();
  const onFocus = () => resumeHeartbeat();
  const onOnline = () => resumeHeartbeat();
  const onOffline = () => {
    clearTimer();
    abortInFlight();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
}

export function startOnlineHeartbeat(): void {
  if (!canUseDom()) return;
  if (running) return;

  running = true;
  installLifecycleListeners();

  if (shouldPause()) {
    clearTimer();
    return;
  }

  void heartbeatOnce();
}

export function stopOnlineHeartbeat(): void {
  running = false;
  clearTimer();
  abortInFlight();
}
